'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

const FloatingShapesBackground = dynamic(
  () => import('@/components/FloatingShapesBackground'),
  { ssr: false },
);

/**
 * Props for `VerticalStepsHighlightedColumn`.
 */
export type VerticalStepsHighlightedColumnProps =
  SliceComponentProps<Content.VerticalStepsHighlightedColumnSlice>;

/**
 * Component for "VerticalStepsHighlightedColumn" Slices.
 *
 * Displays a vertical three-step process with a highlighted left column
 * for categories (Deliverables / Capabilities), each step containing a
 * step title and associated items.
 */
const VerticalStepsHighlightedColumn: FC<
  VerticalStepsHighlightedColumnProps
> = ({ slice }) => {
  const { section_heading, section_subheading, steps, footer_text } =
    slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden text-white px-5 py-16 md:px-16 lg:py-20"
    >
      <FloatingShapesBackground />

      {/* Heading */}
      <div className="mx-auto max-w-[1040px] text-center">
        <PrismicRichText
          field={section_heading}
          components={{
            heading2: ({ children }) => (
              <h2 className="text-[28px] font-medium leading-[1.2] text-white md:text-[36px] lg:text-[42px]">
                {children}
              </h2>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-[#00F557]">
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className="not-italic font-semibold text-[#00F557]">
                {children}
              </em>
            ),
          }}
        />

        {section_subheading && (
          <PrismicRichText
            field={section_subheading}
            components={{
              heading3: ({ children }) => (
                <h3 className="mt-2 text-[22px] font-semibold leading-[1.2] text-[#00F557] md:text-[30px] lg:text-[42px]">
                  {children}
                </h3>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-[#00F557]">
                  {children}
                </strong>
              ),
            }}
          />
        )}
      </div>

      {/* Steps grid */}
      <div className="mx-auto mt-10 flex max-w-[1040px] flex-col gap-4 md:gap-2 lg:mt-[45px] lg:flex-row">
        {/* Left highlighted column — category labels */}
        <div className="hidden w-[192px] shrink-0 flex-col lg:flex">
          {/* Spacer matching step header height */}
          <div className="h-[112px]" />

          {/* Glassmorphism label card */}
          <div
            className="flex flex-col rounded-[20px] border border-white/70 p-6"
            style={{
              backgroundImage:
                'linear-gradient(108deg, rgba(255,255,255,0.47) 0%, rgba(255,255,255,0) 111%)',
              backdropFilter: 'blur(21px)',
            }}
          >
            <div className="flex h-[100px] items-center border-b border-[#bababa]/50 py-3">
              <span className="text-lg font-semibold text-[#00F557]">
                Deliverables
              </span>
            </div>
            <div className="flex h-[100px] items-center py-3">
              <span className="text-lg font-semibold text-[#00F557]">
                Capabilities
              </span>
            </div>
          </div>
        </div>

        {/* Step columns */}
        {steps.map((step, index) => (
          <div
            key={index}
            className="flex flex-col gap-4 rounded-[20px] border border-white/50 p-4 md:gap-6 md:p-6 lg:flex-1"
            style={{
              backgroundImage:
                'linear-gradient(108deg, rgba(173,177,188,0.2) 0%, rgba(0,0,0,0) 111%)',
              backdropFilter: 'blur(21px)',
            }}
          >
            {/* Step title */}
            <div className="flex flex-col items-center justify-center border-b border-white/70 py-4">
              <h4 className="w-full text-xl font-semibold leading-[1.4] text-[#00F557]">
                {step.step_title}
              </h4>
            </div>

            {/* Items */}
            <div className="flex flex-col">
              {/* Deliverable */}
              <div className="flex min-h-[100px] items-center border-b border-white/50 py-3">
                {/* Mobile-only label */}
                <div className="mr-3 lg:hidden">
                  <span className="text-sm font-semibold text-[#00F557]">
                    Deliverables:
                  </span>
                </div>
                <p className="text-base font-normal leading-[1.5] text-white">
                  {step.deliverable_item}
                </p>
              </div>

              {/* Capabilities */}
              <div className="flex min-h-[100px] items-center py-3">
                {/* Mobile-only label */}
                <div className="mr-3 lg:hidden">
                  <span className="text-sm font-semibold text-[#00F557]">
                    Capabilities:
                  </span>
                </div>
                <p className="text-base font-normal leading-[1.5] text-white">
                  {step.capabilities_item}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Footer */}
      {footer_text && (
        <Link
          href="/contact-us"
          className="mx-auto mt-10 block max-w-[1040px] rounded-[20px] p-px lg:mt-[45px] transition-opacity hover:opacity-90"
          style={{
            background:
              'linear-gradient(113deg, rgba(228, 219, 167, 0.50) 0%, rgba(228, 219, 167, 0) 110.84%)',
          }}
        >
          <div
            className="flex items-center justify-center rounded-[19px] px-6 py-4"
            style={{
              background:
                'linear-gradient(113deg, rgba(173, 177, 188, 0.20) 0%, rgba(0, 0, 0, 0.00) 110.84%), #0d1412',
              backdropFilter: 'blur(21px)',
            }}
          >
            <p className="text-center text-base font-normal leading-normal text-white md:text-xl lg:text-[24px]">
              {footer_text}
            </p>
          </div>
        </Link>
      )}
    </section>
  );
};

export default VerticalStepsHighlightedColumn;
